import type { Ring, Vec2, Vec3 } from '../../geo/types';
import { hogeHuisOpenings } from './hogeHuis';
import { buildClaverblat } from './claverblat';
import { isClaverblatQuay } from './surveyed/claverblatPlan';
import { ensureCCW } from '../../geo/polygon';
import type { PaletteModel } from '../../data/types';
import type { Rng } from '../../core/rng';
import type { MeshBuilder, VertexStyle, Frame } from '../mesh/meshBuilder';
import { edgeIdFor } from '../mesh/attributes';
import { sampleSwatch } from '../../data/palette';
import type { Lot, HouseParams } from './types';
import { gableOutline, type GableOutline } from './gables';
import {
  sampleHouseParams,
  getWallColor,
  getRoofColor,
  infillColors,
  sampleLitSwatch,
  HOUSE,
  NO_READINGS,
  type LotReadings,
} from './houseStyle';
import { orientedFace, triangulateRing } from './solids';
import { buildCoping, buildFinial, COPING } from './gableTrim';
import { buildStepCopings } from './stepCopings';
import { courtyardOpenings, openingsFor, type Opening } from './openings';
import type { PartyWalls } from './partyWalls';
import { lotFrame } from './lotFrame';
import { buildFacades, wallFace, type Body } from './houseFacades';
import type { DetailSink } from './detailSink';
import { lotEdgeIndex } from './lotKey';
import {
  houseFrame,
  houseStyles,
  type HouseColors,
  type HouseGeometry,
  type HousePlan,
} from './houseParts';
import { buildHouseRoof } from './houseRoof';
import { buildTimberHouse } from './timberHouse';
import { NO_STREETS, type HouseStreets } from './houseStreets';
import { tintWall } from './wallTint';
import { sampleShop, withTimberLuifel } from './shops';
import { placeWares, sampleWares, wareStyles } from './shopWares';
import { buildStoepen, sampleStoep, stoepWalls } from './stoep';
import { buildLuifel } from './luifel';
import { buildShopRoom, hasShopRoom, shopRoomColors, shopRoomOf } from './shopRoom';
import { anchorBrickHouse, anchorLevels } from './wallAnchors';
import { damrakWaterOpenings, DAMRAK_WATER_RECESS, isDamrakWaterfront } from './damrakWaterfront';
import { housesOnPlot, partyOnPart } from './deepHouse';
import { waterWallOpenings } from './waterWalls';

export type { HouseColors, HouseGeometry, HousePlan } from './houseParts';

// Stepped and spout fronts are separate slabs; the body starts just inside the slab so
// the two solids never share a plane or a vertex.
const SLAB_OVERLAP = 0.02;

function outlineOutward(a: Vec3, b: Vec3): Vec3 {
  return [0, -(b[2] - a[2]), b[1] - a[1]];
}

/**
 * Back face and edges of a stepped or spout slab, from the facade back to `thickness`; its
 * front face, with the openings, is part of the facade. A shop opening goes through it to the
 * front room, so the back face has it too (`holes`, as z and y).
 */
function buildGableSlab(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  gable: GableOutline,
  thickness: number,
  holes: readonly Ring[] = []
): void {
  const half = gable.points[1]![2];
  const outline = gable.points.map((p): Vec2 => [p[2], p[1]]);
  for (const tri of holes.length > 0
    ? triangulateRing(ensureCCW(outline), holes)
    : triangulateRing(outline)) {
    const pts = tri.map((q): Vec3 => [thickness, q[1], q[0]]);
    orientedFace(builder, style, frame, pts, [1, 0, 0], (p) => [half - p[2], p[1]]);
  }
  const pts = gable.points;
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i]!;
    const b = pts[(i + 1) % pts.length]!;
    const side: Vec3[] = [a, b, [thickness, b[1], b[2]], [thickness, a[1], a[2]]];
    orientedFace(builder, style, frame, side, outlineOutward(a, b));
  }
}

/* The shop openings of a front as rings of z (from its left end at -half) and y. */
function shopHolesZY(front: readonly Opening[], half: number): Ring[] {
  return front
    .filter((o) => o.kind === 'shop')
    .map((o): Ring => {
      const [z0, z1, y0, y1] = [o.x - half, o.x + o.width - half, o.y, o.y + o.height];
      return ensureCCW([
        [z0, y0],
        [z1, y0],
        [z1, y1],
        [z0, y1],
      ]);
    });
}

function bodyOf(plan: HousePlan, width: number, depth: number): Body {
  const { params } = plan;
  const eaves = params.storeys * params.storeyHeight;
  const slab = params.frontGable === 'plain' ? 0 : HOUSE.steppedGable.wallThickness;
  return {
    x0: slab > 0 ? slab - SLAB_OVERLAP : 0,
    depth,
    half: width / 2,
    eaves,
    ridge: eaves + (width / 2) * Math.tan(params.pitch),
  };
}

/* A back building's front faces its courtyard, so it keeps the plain body whatever its front is made of. */
function isJettied(plan: HousePlan): boolean {
  return plan.params.frontType === 'timber' && plan.lot.kind === 'house';
}

export function buildHouse(
  builder: MeshBuilder,
  plan: HousePlan,
  details: DetailSink
): HouseGeometry {
  if (isClaverblatQuay(plan.lot)) return buildClaverblat(builder, plan, details);
  if (isJettied(plan)) return buildTimberHouse(builder, plan, details);
  const { lot, params } = plan;
  const { width, depth } = lotFrame(lot);
  const body = bodyOf(plan, width, depth);
  const frame = houseFrame(lot);
  const styles = houseStyles(plan, width, depth, body.eaves);
  const front = gableOutline(params.frontGable, width, body.eaves, params.pitch);
  const slab = params.frontGable === 'plain' ? null : front;
  const layout = lot.kind === 'backhouse' ? courtyardOpenings : openingsFor;
  const spec = { ...params, width, length: depth - body.x0, party: plan.party, shop: plan.shop };
  const ordinary = layout(spec, plan.rng.fork('openings'));
  const waterfront = isDamrakWaterfront(lot);
  const wet = lot.kind === 'backhouse' ? lot.onWater : undefined;
  const openings = waterfront
    ? damrakWaterOpenings(spec, ordinary)
    : hogeHuisOpenings(
        lot,
        spec,
        wet ? waterWallOpenings(spec, ordinary, wet, plan.rng.fork('waterWalls')) : ordinary
      );

  const before = builder.triangleCount;
  const ctx = {
    builder,
    styles,
    frame,
    details,
    edgeId: plan.edgeId,
    openFrontDoor: plan.openFrontDoor,
  };
  buildFacades(ctx, body, openings, {
    slab,
    side: styles.side,
    ...(waterfront ? { backRecess: DAMRAK_WATER_RECESS } : {}),
  });
  if (lot.kind === 'house')
    anchorBrickHouse(
      ctx,
      body,
      anchorLevels(params.storeys, params.storeyHeight),
      openings,
      plan.streets
    );
  placeWares(
    { ...ctx, styles: wareStyles(styles) },
    wallFace([0, 0, -body.half], [-1, 0, 0]),
    openings.front,
    plan.wares
  );
  if (slab)
    buildGableSlab(
      builder,
      styles.wall,
      frame,
      slab,
      HOUSE.steppedGable.wallThickness,
      shopHolesZY(openings.front, body.half)
    );
  buildHouseRoof(builder, styles, frame, body, params, details);
  buildStoepen(
    builder,
    styles.stone,
    frame,
    plan.stoep,
    stoepWalls(0, body.half, depth, plan.party)
  );
  if (plan.shop.luifel)
    buildLuifel(builder, styles.luifel, frame, {
      frontAt: () => 0,
      half: body.half,
      eaves: body.eaves,
    });
  if (hasShopRoom(plan.shop))
    buildShopRoom(builder, styles.room, frame, shopRoomOf(body, 0, params), openings, body);
  /*
   * Coping and finial mark the street gables (STYLE §8). A back building's gable faces its courtyard
   * or gang, and the 28 triangles would cost two thousand back buildings more than the frame affords.
   */
  if (lot.kind === 'house') {
    const bounds = {
      xBack: (slab ? HOUSE.steppedGable.wallThickness : 0) + COPING.back,
      halfWidth: body.half,
    };
    buildCoping(builder, styles.trim, frame, front.coping, bounds);
    if (params.frontGable === 'stepped')
      buildStepCopings(builder, styles.trim, frame, front.coping, bounds);
    buildFinial(builder, styles.trim, frame, front.apex, COPING.finialRadius);
  }
  return {
    eavesHeight: body.eaves,
    ridgeHeight: Math.max(front.ridgeY, body.ridge),
    triangleCount: builder.triangleCount - before,
  };
}

export function houseColors(palette: PaletteModel, params: HouseParams, rng: Rng): HouseColors {
  return {
    wall: tintWall(getWallColor(params.frontColour, palette, rng.fork('wall'))),
    roof: getRoofColor(params.roofMaterial, palette, rng.fork('roof')),
    trim: sampleSwatch(palette.highlightWhite, rng.fork('trim')),
    ...infillColors(palette),
    stone: sampleLitSwatch(palette.towerStone, rng.fork('stone')),
    room: shopRoomColors(palette),
    iron: palette.lineworkDark.median.linear,
  };
}

export function buildHouseOnLot(
  builder: MeshBuilder,
  lot: Lot,
  palette: PaletteModel,
  rng: Rng,
  party: PartyWalls,
  details: DetailSink,
  streets: HouseStreets = NO_STREETS,
  readings: LotReadings = NO_READINGS,
  openFrontDoor = false,
  /** How far below the floor the street lies under the stoep (foundation.stoepFoot). */
  stoepFoot = 0
): HouseGeometry {
  const [front, back] = housesOnPlot(lot);
  if (front && back) {
    const { depth } = lotFrame(lot);
    const frontDepth = lotFrame(front).depth;
    const street = buildHouseOnLot(
      builder,
      front,
      palette,
      rng,
      partyOnPart(party, { from: 0, to: frontDepth }, depth),
      details,
      streets,
      readings,
      openFrontDoor,
      stoepFoot
    );
    const behind = buildHouseOnLot(
      builder,
      back,
      palette,
      rng.fork('backHouse'),
      partyOnPart(party, { from: depth - lotFrame(back).depth, to: depth }, depth),
      details,
      NO_STREETS,
      readings
    );
    return {
      ...street,
      ridgeHeight: Math.max(street.ridgeHeight, behind.ridgeHeight),
      triangleCount: street.triangleCount + behind.triangleCount,
    };
  }
  const params = sampleHouseParams(lot, rng, readings, lotFrame(lot).width);
  const colors = houseColors(palette, params, rng);
  const shop = withTimberLuifel(sampleShop(lot, streets, rng), params.frontType, streets);
  const stoep = { ...sampleStoep(lot, streets, shop, rng), foot: stoepFoot };
  const wares = sampleWares(shop, streets, rng);
  const edgeId = edgeIdFor(lotEdgeIndex(lot));
  const plan: HousePlan = {
    openFrontDoor,
    lot,
    params,
    colors,
    edgeId,
    seed: rng.next(),
    party,
    streets,
    shop,
    stoep,
    wares,
    rng,
  };
  return {
    ...buildHouse(builder, plan, details),
    frontStep: { reach: stoep.front, height: stoep.height },
  };
}
